import {
  FEATURE_PICKUP_STREAK,
  ACTIVE_TECH_DEFINITIONS,
  COMBO_WINDOW_SECONDS,
  GENERATED_TILE_VIEW_RADIUS_X,
  GENERATED_TILE_VIEW_RADIUS_Y,
  HOME_TILE_X,
  HOME_TILE_Y,
  RESOURCE_IDS,
  TILE_SIZE,
  TOOL_RECIPE_DEFINITIONS,
  canAfford,
  streakThreshold,
} from '../domain/content.js';
import type {
  BuildingState,
  Direction,
  GameState,
  PanelCommand,
  ViewActionTarget,
  ViewBuilding,
  ViewBuildingPanel,
  ViewDebugOption,
  ViewModel,
  ViewTechOption,
  ViewToolRecipe,
  ViewTile,
} from '../contract.js';
import {
  BUILD_COSTS,
  ACTIVE_BUILD_OPTIONS,
  buildingAcceptsText,
  buildingById,
  buildingProgress,
  buildingStatus,
  canAffordBuilding,
  canCollectOutput,
  canDepositSelected,
  canRemoveBuilding,
  canPlacePendingBuilding,
  getBuildingAtTile,
} from './buildings.js';
import { hasEmptySlot, hasUsableItem } from './inventory.js';
import { isGooSource, isGooTile } from './goo.js';
import { generatedTileAmount, generatedTileVariant, getTileAmount, isPermanentWall, isRevealed, pixelToTile } from './tiles.js';
import { zoneAtTile, zoneColor, zoneProgress } from './zones.js';

const DEBUG_OPTIONS: ViewDebugOption[] = [
  { command: 'grantTechPoint', name: '+1 Tech Point', description: 'Add one tech point for testing upgrade paths.' },
  { command: 'grantScrap', name: '+20 Scrap', description: 'Add one full scrap stack to the player inventory.' },
  { command: 'grantPlastic', name: '+20 Plastic', description: 'Add one full plastic stack to the player inventory.' },
  { command: 'grantGlass', name: '+20 Glass', description: 'Add one full glass stack to the player inventory.' },
  { command: 'grantBiomass', name: '+20 Biomass', description: 'Add one full biomass stack to the player inventory.' },
  { command: 'grantAllResources', name: '+20 All Resources', description: 'Add one stack of every resource when inventory space allows.' },
];

export function getViewModel(state: GameState): ViewModel {
  const playerTileX = pixelToTile(state.player.x);
  const playerTileY = pixelToTile(state.player.y);
  const tiles: ViewTile[] = [];
  const buildings = state.buildings.map((building) => createBuildingView(building));

  for (let y = playerTileY - GENERATED_TILE_VIEW_RADIUS_Y; y <= playerTileY + GENERATED_TILE_VIEW_RADIUS_Y; y++) {
    for (let x = playerTileX - GENERATED_TILE_VIEW_RADIUS_X; x <= playerTileX + GENERATED_TILE_VIEW_RADIUS_X; x++) {
      const amount = getTileAmount(state, x, y);
      const wall = isPermanentWall(x, y);
      const goo = isGooTile(state, x, y);
      const revealed = isRevealed(state, x, y);
      const visible = revealed && isVisibleNow(state, playerTileX, playerTileY, x, y);
      const zone = zoneAtTile(x, y);
      tiles.push({
        x,
        y,
        amount: revealed ? amount : 0,
        maxAmount: revealed ? generatedTileAmount(state.seed, x, y) : 0,
        variant: generatedTileVariant(state.seed, x, y),
        blocked: wall || amount > 0,
        wall,
        goo,
        gooSource: isGooSource(x, y) && amount > 0,
        restored: amount <= 0 && !goo && generatedTileAmount(state.seed, x, y) > 0,
        visible,
        revealed,
        spriteId: pileSpriteId(amount),
        zoneId: zone?.id ?? null,
        zoneColor: zoneColor(zone?.id ?? null),
      });
    }
  }

  const inventorySlots = state.inventory.slots.map((item, index) => ({
    index,
    selected: index === state.player.selectedSlot,
    item: item === null ? null : { ...item },
  }));

  return {
    tick: state.tick,
    camera: {
      x: state.player.x,
      y: state.player.y,
    },
    player: {
      ...state.player,
      tileX: playerTileX,
      tileY: playerTileY,
    },
    actionTarget: createActionTarget(state, playerTileX, playerTileY),
    inventory: {
      slots: inventorySlots,
      used: state.inventory.slots.filter((slot) => slot !== null).length,
      capacity: state.inventory.slots.length,
    },
    home: {
      tileX: HOME_TILE_X,
      tileY: HOME_TILE_Y,
      x: HOME_TILE_X * TILE_SIZE + TILE_SIZE * 0.5,
      y: HOME_TILE_Y * TILE_SIZE + TILE_SIZE * 0.5,
    },
    resources: { ...state.resources },
    resourceOrder: [...RESOURCE_IDS],
    selectedTool: {
      id: state.player.selectedTool,
      label: toolLabel(state.player.selectedTool),
      available: selectedToolAvailable(state),
    },
    buildings,
    mobileUnits: state.mobileUnits.map((unit) => ({
      id: unit.id,
      type: unit.type,
      x: unit.x,
      y: unit.y,
      mode: unit.mode,
      cargo: unit.cargo.filter((slot) => slot !== null).length,
      cargoCapacity: unit.cargo.length,
    })),
    buildingPanel: createBuildingPanel(state, inventorySlots),
    buildMenu: {
      open: state.ui.buildMenuOpen,
      pendingBuildType: state.ui.pendingBuildType,
      pendingCost: state.ui.pendingBuildType === null ? null : { ...BUILD_COSTS[state.ui.pendingBuildType] },
      focusedIndex: state.ui.buildMenuFocus,
      options: ACTIVE_BUILD_OPTIONS.map((option) => ({
        ...option,
        cost: { ...BUILD_COSTS[option.type] },
        canBuild: canAffordBuilding(state, option.type),
      })),
    },
    inventoryPanel: {
      open: state.ui.inventoryOpen,
      selectedSlot: state.player.selectedSlot,
      focusedSlot: state.ui.inventoryFocus,
      slots: inventorySlots,
    },
    techTree: {
      open: state.ui.techTreeOpen,
      points: state.tech.points,
      focusedIndex: state.ui.techTreeFocus,
      options: createTechOptions(state),
    },
    toolsPanel: {
      open: state.ui.toolsPanelOpen,
      focusedIndex: state.ui.toolsPanelFocus,
      recipes: createToolRecipes(state),
    },
    debugPanel: {
      open: state.ui.debugPanelOpen,
      focusedIndex: state.ui.debugPanelFocus,
      options: DEBUG_OPTIONS,
    },
    tiles,
    currentZone: zoneProgress(state, zoneAtTile(playerTileX, playerTileY)),
    gather: state.player.gather === null
      ? null
      : {
        active: true,
        tileX: state.player.gather.targetTileX,
        tileY: state.player.gather.targetTileY,
        progress: Math.min(1, state.player.gather.elapsedSec / state.player.gather.durationSec),
      },
    streak: {
      enabled: state.features.pickupStreak && FEATURE_PICKUP_STREAK,
      value: state.streak.value,
      tier: state.streak.tier,
      timeRemainingSec: state.streak.timeRemainingSec,
      nextTierAt: streakThreshold(state.streak.tier + 1),
      windowSec: COMBO_WINDOW_SECONDS,
    },
    fxEvents: state.fxEvents.map((event) => ({ ...event })),
    stats: { ...state.stats },
    statusMessage: state.ui.statusMessage,
  };
}

function createToolRecipes(state: GameState): ViewToolRecipe[] {
  return TOOL_RECIPE_DEFINITIONS.map((recipe) => {
    const unlocked = state.tech.levels[recipe.requiredTech] > 0;
    const owned = state.inventory.slots.filter((slot) => slot?.kind === 'item' && slot.itemId === recipe.resultItemId).length;
    const uniqueBlocked = recipe.unique && owned > 0;
    const hasResources = canAfford(state.resources, recipe.cost);
    const canCraft = unlocked && !uniqueBlocked && hasResources && hasEmptySlot(state);
    return {
      id: recipe.id,
      name: recipe.name,
      description: recipe.description,
      resultItemId: recipe.resultItemId,
      cost: { ...recipe.cost },
      unlocked,
      canCraft,
      owned,
      unique: recipe.unique,
      status: toolRecipeStatus(unlocked, uniqueBlocked, hasResources, hasEmptySlot(state)),
    };
  });
}

function toolRecipeStatus(unlocked: boolean, uniqueBlocked: boolean, hasResources: boolean, hasSlot: boolean): string {
  if (!unlocked) return 'Locked';
  if (uniqueBlocked) return 'Owned';
  if (!hasSlot) return 'No empty slot';
  if (!hasResources) return 'Need resources';
  return 'Ready';
}

function createTechOptions(state: GameState): ViewTechOption[] {
  return ACTIVE_TECH_DEFINITIONS.map((tech) => {
    const level = state.tech.levels[tech.id];
    const maxed = level >= tech.maxLevel;
    return {
      id: tech.id,
      name: tech.name,
      description: tech.description,
      category: tech.category,
      level,
      maxLevel: tech.maxLevel,
      cost: tech.cost,
      canBuy: !maxed && state.tech.points >= tech.cost,
      locked: false,
    };
  });
}

function createActionTarget(state: GameState, playerTileX: number, playerTileY: number): ViewActionTarget {
  const delta = directionVector(state.player.facing);
  const tileX = playerTileX + delta.x;
  const tileY = playerTileY + delta.y;
  let kind: ViewActionTarget['kind'] = 'empty';
  let label = 'Nothing';

  if (state.player.selectedTool !== 'hands' && selectedToolAvailable(state)) {
    kind = 'harvest';
    label = state.player.selectedTool === 'grenade' ? 'Throw Grenade' : 'Burn Trash';
  } else if (state.ui.pendingBuildType !== null) {
    kind = canPlacePendingBuilding(state, tileX, tileY, playerTileX, playerTileY) ? 'place' : 'blocked';
    label = kind === 'place' ? `Place ${buildingLabel(state.ui.pendingBuildType)}` : 'Blocked';
  } else {
    const building = getBuildingAtTile(state, tileX, tileY);
    if (building !== undefined) {
      kind = 'building';
      label = `Open ${building.name}`;
    } else if (isPermanentWall(tileX, tileY)) {
      kind = 'blocked';
      label = 'Ruins Wall';
    } else if (getTileAmount(state, tileX, tileY) > 0) {
      kind = 'harvest';
      label = 'Gather';
    } else if (isGooTile(state, tileX, tileY)) {
      kind = 'harvest';
      label = 'Clean Spill';
    }
  }

  return {
    tileX,
    tileY,
    x: tileX * TILE_SIZE + TILE_SIZE * 0.5,
    y: tileY * TILE_SIZE + TILE_SIZE * 0.5,
    kind,
    label,
  };
}

function createBuildingView(building: BuildingState): ViewBuilding {
  return {
    id: building.id,
    type: building.type,
    name: building.name,
    tileX: building.tileX,
    tileY: building.tileY,
    width: building.width,
    height: building.height,
    x: building.tileX * TILE_SIZE + (building.width * TILE_SIZE) * 0.5,
    y: building.tileY * TILE_SIZE + (building.height * TILE_SIZE) * 0.5,
    inboxUsed: building.inbox.filter((slot) => slot !== null).length,
    inboxCapacity: building.inbox.length,
    outboxUsed: building.outbox.filter((slot) => slot !== null).length,
    outboxCapacity: building.outbox.length,
    storageUsed: building.storage.filter((slot) => slot !== null).length,
    storageCapacity: building.storage.length,
    active: building.active === null
      ? null
      : {
        ...building.active,
        item: { ...building.active.item },
      },
    status: buildingStatus(building),
    progress: buildingProgress(building),
    canRemove: canRemoveBuilding(building),
  };
}

function createBuildingPanel(state: GameState, inventorySlots: ViewBuildingPanel['playerInventory']): ViewBuildingPanel | null {
  const building = buildingById(state, state.ui.openPanelBuildingId);
  if (building === undefined) return null;
  return {
    ...createBuildingView(building),
    inbox: building.inbox.map((item) => (item === null ? null : { ...item })),
    outbox: building.outbox.map((item) => (item === null ? null : { ...item })),
    storage: building.storage.map((item) => (item === null ? null : { ...item })),
    playerInventory: inventorySlots,
    accepts: buildingAcceptsText(building),
    canDepositSelected: canDepositSelected(state, building),
    canCollectOutput: canCollectOutput(building),
    canRemove: canRemoveBuilding(building),
    focusedCommand: panelCommandForFocus(state.ui.buildingPanelFocus),
  };
}

function panelCommandForFocus(focus: number): PanelCommand {
  return (['depositSelected', 'depositAll', 'collectOutput', 'removeBuilding', 'close'] as const)[wrap(focus, 5)];
}

function buildingLabel(type: string): string {
  if (type === 'storage') return 'Storage Depot';
  if (type === 'gatherer') return 'Gatherer';
  if (type === 'carrier_drone') return 'Carrier Drone';
  if (type === 'drill') return 'Drill';
  return 'Recycler';
}

function wrap(value: number, count: number): number {
  if (count <= 0) return 0;
  return ((value % count) + count) % count;
}

function isVisibleNow(state: GameState, fromX: number, fromY: number, toX: number, toY: number): boolean {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const dist = Math.hypot(dx, dy);
  if (dist > 7) return false;
  const steps = Math.max(1, Math.ceil(dist * 2));
  let budget = 8;
  for (let i = 1; i <= steps; i++) {
    const tx = Math.round(fromX + (dx * i) / steps);
    const ty = Math.round(fromY + (dy * i) / steps);
    if (isPermanentWall(tx, ty)) return false;
    budget -= 1 + getTileAmount(state, tx, ty) * 0.55;
    if (budget < 0) return false;
  }
  return true;
}

function selectedToolAvailable(state: GameState): boolean {
  if (state.player.selectedTool === 'hands') return true;
  if (state.player.selectedTool === 'grenade') return state.tech.levels.grenade > 0 && hasUsableItem(state, 'grenade');
  return state.tech.levels.flamethrower > 0 && hasUsableItem(state, 'flamethrower');
}

function toolLabel(tool: GameState['player']['selectedTool']): string {
  if (tool === 'grenade') return 'Grenade';
  if (tool === 'flamethrower') return 'Flamethrower';
  return 'Hands';
}

function pileSpriteId(amount: number): ViewTile['spriteId'] {
  switch (amount) {
    case 0:
      return 'garbage_pile_1';
    case 1:
      return 'garbage_pile_1';
    case 2:
      return 'garbage_pile_2';
    case 3:
      return 'garbage_pile_3';
    case 4:
      return 'garbage_pile_4';
    case 5:
      return 'garbage_pile_5';
    default:
      return 'garbage_pile_6';
  }
}

function directionVector(direction: Direction): { x: -1 | 0 | 1; y: -1 | 0 | 1 } {
  switch (direction) {
    case 'north':
      return { x: 0, y: -1 };
    case 'east':
      return { x: 1, y: 0 };
    case 'south':
      return { x: 0, y: 1 };
    case 'west':
      return { x: -1, y: 0 };
  }
}
